/**
 * TOSS Web Application — the notifications popover, one panel for both audiences
 *
 * What drops out of the bell on a wide screen. The customer portal has drawn this shape since
 * release Р2; on 2026-08-26 the owner pointed at the internal one and said to make it the same —
 * it had grown 40 px round type icons, a coloured badge per row and 64 px rows, so five
 * notifications filled a panel in which the cabinet shows eight and still has room.
 *
 * Same rule as the rest of this layer: it never sees a notification. It takes rows that are
 * already three strings and a flag ({@link NotificationRowView}), which is what keeps it honest —
 * the two DTOs behind those rows are not interchangeable. The internal one carries the recipient,
 * the entity ids and a type from a seven-value staff dictionary; the customer's carries none of
 * that. A panel that could read either would be a panel that could print the wrong one.
 *
 * **What it deliberately is not.** No infinite scroll, no per-row controls, no internal scrollbar:
 * a panel that grows those has become a screen, and both surfaces already have that screen — the
 * foot of this one leads to it. One row of chips IS allowed since 24.09.2026 (owner, variant C1 of
 * the priority split): «Все / Важные / Непрочитанные» choose which eight rows the panel shows, they
 * do not turn it into a second feed. A surface that passes no `filters` gets the panel as before.
 *
 * **The dot is the only colour** (C1). Unread rows share one wash whatever their weight; the dot
 * says which sector: red for a priority row, primary for the rest, and a red RING once a priority
 * row has been read — so an answered client application still reads as one, while an ordinary read
 * row has no mark at all. The red count on the bell counts the same red dots.
 */

import type { RefObject } from 'react';
import { Link } from 'react-router-dom';
import { ChoiceChip, DropdownPortal, Spinner } from '@/components/ui';

// ============================================================================
// Constants
// ============================================================================

/**
 * Rows drawn in the panel. Eight is what fits above the fold on a 900 px window without the
 * panel itself scrolling; beyond that the archive is one click away and is built for it.
 */
export const NOTIFICATIONS_POPOVER_LIMIT = 8;

// ============================================================================
// Types
// ============================================================================

export interface NotificationRowView {
  id: string;
  /** The headline, one line, truncated. */
  title: string;
  /** The second line: whatever the surface thinks explains this row, plus its age. */
  meta: string;
  isRead: boolean;
  /** Priority sector: a red dot while unread, a red ring once read. Absent = ordinary. */
  isPriority?: boolean;
}

/** One chip of the row above the list. */
export interface NotificationsPopoverFilterOption {
  id: string;
  label: string;
  /** Printed after the label when above zero. */
  count?: number;
}

/** The chip row — the surface owns the value and what each chip means. */
export interface NotificationsPopoverFilters {
  options: NotificationsPopoverFilterOption[];
  value: string;
  onChange: (id: string) => void;
}

export interface NotificationsPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLElement | null>;
  items: NotificationRowView[];
  isLoading: boolean;
  /** Whether to offer «Прочитать все» — the surface counts, this panel only asks. */
  hasUnread: boolean;
  onMarkAll: () => void;
  isMarkingAll?: boolean;
  /** A row was activated. Marking read and navigating both belong to the surface. */
  onActivate: (id: string) => void;
  /** Where «Все уведомления» leads — each audience has its own feed. */
  allHref: string;
  /** Optional chip row under the heading; omitted = no row (the customer portal). */
  filters?: NotificationsPopoverFilters;
  /** What an empty list says — a filtered list is empty for its own reason. */
  emptyText?: string;
}

// ============================================================================
// Component
// ============================================================================

export function NotificationsPopover({
  isOpen,
  onClose,
  triggerRef,
  items,
  isLoading,
  hasUnread,
  onMarkAll,
  isMarkingAll = false,
  onActivate,
  allHref,
  filters,
  emptyText = 'Уведомлений пока нет',
}: NotificationsPopoverProps): JSX.Element {
  const visible = items.slice(0, NOTIFICATIONS_POPOVER_LIMIT);

  return (
    <DropdownPortal
      isOpen={isOpen}
      onClose={onClose}
      triggerRef={triggerRef}
      placement="bottom-end"
      className="w-[380px] max-w-[calc(100vw-2rem)] app-surface app-surface-outlined shadow-[var(--shadow-overlay)] overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-[var(--color-border)]">
        <div className="flex items-center justify-between gap-2">
          <span className="text-base font-semibold text-[var(--color-text-primary)]">
            Уведомления
          </span>

          {hasUnread && (
            <button
              type="button"
              onClick={onMarkAll}
              disabled={isMarkingAll}
              className="text-sm font-medium text-[var(--color-primary-600)] hover:text-[var(--color-primary-700)] disabled:opacity-60 transition-colors"
            >
              Прочитать все
            </button>
          )}
        </div>

        {filters && (
          <div className="flex items-center gap-2 mt-2.5" role="group" aria-label="Фильтр уведомлений">
            {filters.options.map((option) => (
              <ChoiceChip
                key={option.id}
                isActive={filters.value === option.id}
                onClick={() => filters.onChange(option.id)}
              >
                {option.label}
                {option.count ? ` ${option.count > 99 ? '99+' : option.count}` : ''}
              </ChoiceChip>
            ))}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="sm" />
        </div>
      ) : visible.length === 0 ? (
        <p className="px-4 py-8 text-sm text-center text-[var(--color-text-secondary)]">
          {emptyText}
        </p>
      ) : (
        <ul>
          {visible.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onActivate(item.id)}
                className={`
                  w-full text-left flex items-start gap-2.5 px-4 py-2.5
                  transition-colors hover:bg-[var(--color-bg-secondary)]
                  focus:outline-none focus-visible:bg-[var(--color-bg-secondary)]
                  ${item.isRead ? '' : 'bg-[var(--color-primary-50)]'}
                `}
              >
                {/* Reserved on read rows too, so titles line up down the panel. */}
                <span className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${dotClass(item)}`} aria-hidden="true" />

                <span className="min-w-0 flex-1">
                  <span
                    className={`block text-sm font-medium truncate ${
                      item.isRead
                        ? 'text-[var(--color-text-secondary)]'
                        : 'text-[var(--color-text-primary)]'
                    }`}
                  >
                    {item.isPriority && <span className="sr-only">Важное: </span>}
                    {item.title}
                  </span>

                  {/* Explanation and age share one line: the panel is 380 px wide and the full
                      sentence belongs on the page behind «Все уведомления». */}
                  {item.meta && (
                    <span className="block text-sm text-[var(--color-text-disabled)] truncate">
                      {item.meta}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-[var(--color-border)]">
        <Link
          to={allHref}
          onClick={onClose}
          className="block px-4 py-2.5 text-sm text-center font-medium text-[var(--color-primary-600)] hover:bg-[var(--color-bg-secondary)] transition-colors"
        >
          Все уведомления
        </Link>
      </div>
    </DropdownPortal>
  );
}

/**
 * The row's mark (C1, 24.09.2026): red / primary while unread, a red ring on a read priority row,
 * nothing on a read ordinary one. `border` rather than an inset shadow — shadow means «floating» in
 * this product, never «important».
 */
function dotClass(item: NotificationRowView): string {
  if (!item.isRead) {
    return item.isPriority ? 'bg-[var(--color-error)]' : 'bg-[var(--color-primary-500)]';
  }
  return item.isPriority ? 'border-[1.5px] border-[var(--color-error)]' : 'bg-transparent';
}

export default NotificationsPopover;
